import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from 'react'
import deepEqual from 'deep-equal'

import { APIRequestContext } from '../../../../wrappers/APIRequestContext'
import withConfig from '../../../../wrappers/withConfig'
import actions from '../../../../../utils/submissions/actions'
import toast from '../../../../elem/Toast'

import {
    getQueryStringFromParams,
    removeOrderAndPageParams,
} from '../../../../../utils/params'
import { DataContext } from '../../DataContext'
import { debounce } from 'debounce'
import getFilterParamsFromForm from '../../../../../utils/form/getFilterParamsFromForm'
import { set } from 'lodash'

const FacilityAssignmentContext = createContext(null)

const FacilityAssignmentContextProvider = ({ config, children }) => {
    const [loadingPossibleFacilities, setLoadingPossibleFacilities] = useState(
        false
    )
    const [loadingConfig, setLoadingConfig] = useState(true)
    const { authenticatedFetch } = useContext(APIRequestContext)
    const {
        submissionState,
        setSubmissionState,
        formType,
        activePanel,
    } = useContext(DataContext)
    const [facilityFilterConfig, setFacilityFilterConfig] = useState([])
    const [facilityListConfig, setFacilityListConfig] = useState([])
    const [assignedFacilityData, setAssignedFacilityData] = useState([])
    const [possibleFacilityData, setPossibleFacilityData] = useState([])
    const [possibleFacilityMetadata, setPossibleFacilityMetadata] = useState({})
    const [
        highlightedPossibleFacilities,
        setHighlightedPossibleFacilities,
    ] = useState([])
    const [
        highlightedAssignedFacilities,
        setHighlightedAssignedFacilities,
    ] = useState([])
    const [filterResultsCount, setFilterResultsCount] = useState()
    const [filterResultsLoading, setFilterResultsLoading] = useState()
    const facilityAccessor = `uploadFacilityMerge`

    const [params, setParams] = useState({
        possibleFacilities: {
            Page: 1,
            PageSize: 50,
        },
        assignedFacilities: {
            PageSize: 50,
        },
    })
    const possibleFacilityParams = useRef({})
    const [
        possibleFacilityAbortController,
        setPossibleFacilityAbortController,
    ] = useState(new AbortController())
    const [
        filterResultsAbortController,
        setFilterResultsAbortController,
    ] = useState(new AbortController())
    const { API_URL, ID_COLUMN } = config

    useEffect(() => {
        setLoadingConfig(true)
        authenticatedFetch(`${API_URL}/${formType.APILink}/facilityConfig`)
            .then(async response => {
                if (response.ok) {
                    return response.json()
                } else {
                    const error = await response.text()
                    throw new Error(error)
                }
            })
            .then(response => {
                const list = response.filter(x => x.DisplayInTable)
                setFacilityListConfig(list)
                const filters = response.filter(x => x.DisplayInFilter)
                setFacilityFilterConfig(filters)
            })
            .catch(e => {
                toast({
                    level: 'error',
                    message:
                        'Possible Facilities: ' +
                        (e.message
                            ? e.message
                            : 'Unable to connect to the server. Please try again later.'),
                })
            })
            .finally(() => {
                setLoadingConfig(false)
            })
    }, [formType])

    const fetchPossibleFacilities = useCallback(
        (params, uploadId) => {
            // const assignedFacilityIds = assignedFacilityData
            //     .map(x => x[ID_COLUMN])
            //     .join(',')
            setLoadingPossibleFacilities(true)
            const abort = new AbortController()
            setPossibleFacilityAbortController(abort)
            const paramString = getQueryStringFromParams(params)
            authenticatedFetch(
                `${API_URL}/${formType.APILink}/possibleFacilities?uploadId=${uploadId}&${paramString}`,
                {
                    method: 'GET',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Headers':
                            'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                    },
                    signal: abort.signal,
                }
            )
                .then(async response => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then(response => {
                    setPossibleFacilityData(response.data)
                    setPossibleFacilityMetadata(response.meta)
                })
                .catch(e => {
                    if (e.name !== 'AbortError') {
                        toast({
                            level: 'error',
                            message:
                                'Possible Sample Point List: ' +
                                (e.message
                                    ? e.message
                                    : 'Unable to connect to the server. Please try again later.'),
                        })
                    }
                })
                .finally(() => {
                    setLoadingPossibleFacilities(false)
                })
        },
        [formType, ID_COLUMN]
    )

    const setAsPrimary = useCallback(() => {
        if (
            highlightedPossibleFacilities &&
            highlightedPossibleFacilities.length === 1
        ) {
            const primaryFacility = highlightedPossibleFacilities[0]
            setPossibleFacilityData(prev => {
                const updated = prev.map(facility => ({
                    ...facility,
                    IsPrimary:
                        facility[ID_COLUMN] === primaryFacility[ID_COLUMN],
                }))
                return updated
            })
        }
    }, [highlightedPossibleFacilities])

    const Remove = useCallback(() => {
        if (
            highlightedPossibleFacilities &&
            highlightedPossibleFacilities.length >= 1
        ) {
            setPossibleFacilityData(prev => {
                const updated = prev.filter(
                    facility =>
                        !highlightedPossibleFacilities
                            .map(x => x[ID_COLUMN])
                            .includes(facility[ID_COLUMN])
                )
                return updated
            })
        }
    }, [highlightedPossibleFacilities])

    //keep highlighted possible facilities in sync with possible facility data - if a highlighted facility is removed from the possible list, remove it from the highlighted state as well
    useEffect(() => {
        setHighlightedPossibleFacilities(prev =>
            prev.filter(highlightedFacility =>
                possibleFacilityData.some(
                    facility =>
                        facility[ID_COLUMN] === highlightedFacility[ID_COLUMN]
                )
            )
        )
    }, [possibleFacilityData])

    const saveFacilityMergeData = useCallback(
        async d => {
            return authenticatedFetch(`${API_URL}/${formType.APILink}`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers':
                        'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                },
                body: JSON.stringify({
                    ...submissionState,
                    uploadFacilityMerge: d,
                }),
            }).then(async response => {
                if (response.ok) {
                    return response.json()
                } else {
                    const error = await response.text()
                    throw new Error(error)
                }
            })
        },
        [authenticatedFetch, formType, submissionState]
    )

    // fetch possible facility data on parameter or assigned facility data changes
    useEffect(() => {
        const uploadId =
            submissionState &&
            submissionState.upload &&
            submissionState.upload.UploadId
        if (params && params.possibleFacilities && uploadId) {
            const associatedParams = params.possibleFacilities
            if (!deepEqual(possibleFacilityParams.current, associatedParams)) {
                possibleFacilityAbortController.abort()
                fetchPossibleFacilities(params, uploadId)
                possibleFacilityParams.current = associatedParams
            }
        }
    }, [
        params,
        possibleFacilityAbortController,
        submissionState,
        activePanel,
    ])

    const fetchResultsDebounce = debounce(
        (e, formData) => {
            // query for the updated count
            const newParams = {
                ...params,
                possibleFacilities: {
                    ...getFilterParamsFromForm(formData),
                },
            }
            const abort = new AbortController()
            setFilterResultsAbortController(abort)
            const paramString = getQueryStringFromParams(newParams)
            authenticatedFetch(
                `${API_URL}/${formType.APILink}/possibleFacilities/count?${paramString}`,
                { signal: abort.signal }
            )
                .then(async response => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then(response => {
                    const { meta } = response
                    const { count } = meta
                    setFilterResultsCount(count)
                    setFilterResultsLoading(false)
                })
                .catch(e => {
                    if (e.name !== 'AbortError') {
                        toast({
                            level: 'error',
                            message:
                                'Sample List: ' +
                                (e.message
                                    ? e.message
                                    : 'Unable to connect to the server. Please try again later.'),
                        })
                        setFilterResultsLoading(false)
                    }
                })
        },
        3000,
        false
    )

    const fetchResultsCount = useCallback(
        (e, formData) => {
            const uploadId =
                submissionState &&
                submissionState.upload &&
                submissionState.upload.UploadId
            if (uploadId) {
                filterResultsAbortController.abort()
                setFilterResultsLoading(true)
                fetchResultsDebounce.clear()
                fetchResultsDebounce(e, formData)
            }
        },
        [filterResultsAbortController, submissionState]
    )

    const updateParams = (newParams, field) => {
        setParams(previousParams => ({
            ...previousParams,
            [field]: {
                ...newParams,
            },
        }))
    }

    return (
        <FacilityAssignmentContext.Provider
            value={{
                loadingPossibleFacilities,
                loadingConfig,
                facilityFilterConfig,
                facilityListConfig,
                params,
                setParams: updateParams,
                possibleFacilityData,
                possibleFacilityMetadata,
                setPossibleFacilityData,
                assignedFacilityData,
                setAssignedFacilityData,
                facilityAccessor,
                highlightedAssignedFacilities,
                setHighlightedAssignedFacilities,
                highlightedPossibleFacilities,
                setHighlightedPossibleFacilities,
                fetchResultsCount,
                filterResultsCount,
                filterResultsLoading,
                saveFacilityMergeData,
                setAsPrimary,
                Remove,
            }}
        >
            {children}
        </FacilityAssignmentContext.Provider>
    )
}

export { FacilityAssignmentContext }
export default withConfig(FacilityAssignmentContextProvider)
